import React, { useState, useEffect, useMemo } from 'react';
import { VenetianMask } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import TermsModal from './TermsModal';
import '../styles/AuthModal.css';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthModal = ({ isOpen, onClose, mode: initialMode = 'login', onSuccess }) => {
  const { t } = useTranslation();
  
  const SECURITY_QUESTIONS = useMemo(() => [
    t('account.securityQuestions.q1'),
    t('account.securityQuestions.q2'),
    t('account.securityQuestions.q3'),
    t('account.securityQuestions.q4'),
    t('account.securityQuestions.q5'),
    t('account.securityQuestions.q6'),
    t('account.securityQuestions.q7'),
    t('account.securityQuestions.q8')
  ], [t]);
  
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Terms acceptance state
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsData, setTermsData] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [acceptedTermsVersion, setAcceptedTermsVersion] = useState(null);
  const [acceptedTermsHash, setAcceptedTermsHash] = useState(null);
  
  // Security questions for registration
  const [securityQuestion1, setSecurityQuestion1] = useState('');
  const [securityQuestion2, setSecurityQuestion2] = useState('');
  const [answer1, setAnswer1] = useState('');
  const [answer2, setAnswer2] = useState('');
  
  const filteredQuestions2 = useMemo(
    () => SECURITY_QUESTIONS.filter(q => q !== securityQuestion1),
    [SECURITY_QUESTIONS, securityQuestion1]
  );
  
  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1: enter email, 2: answer questions + new password
  const [fetchedQ1, setFetchedQ1] = useState('');
  const [fetchedQ2, setFetchedQ2] = useState('');
  const [forgotAnswer1, setForgotAnswer1] = useState('');
  const [forgotAnswer2, setForgotAnswer2] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const { login, register, loginWithGoogle, loginAsGuest } = useAuth();

  const handleGuestLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await loginAsGuest();
      if (result.success) {
        if (onSuccess) onSuccess();
        onClose();
      } else {
        setError(result.error);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTerms = async () => {
    try {
      const currentLanguage = localStorage.getItem('chessKingdomLanguage') || 'en';
      const response = await fetch(`${BACKEND_URL}/api/terms/current?language=${currentLanguage}`);
      const data = await response.json();
      if (data.success) {
        setTermsData(data.terms);
      }
    } catch (error) {
      logger.error('Failed to fetch terms:', error);
    }
  };
  
  // Reset forgot password state when switching modes
  useEffect(() => {
    if (mode !== 'forgot') {
      setResetStep(1);
      setForgotEmail('');
      setFetchedQ1('');
      setFetchedQ2('');
      setForgotAnswer1('');
      setForgotAnswer2('');
      setNewPassword('');
    }
    setError('');
  }, [mode]);
  
  const handleSwitchToRegister = () => {
    if (!termsAccepted) {
      fetchTerms();
      setShowTermsModal(true);
    } else {
      setMode('register');
    }
  };
  
  const handleTermsAccept = async (termsInfo) => {
    setTermsAccepted(true);
    setAcceptedTermsVersion(termsInfo.terms_version);
    setAcceptedTermsHash(termsInfo.terms_hash);
    setShowTermsModal(false);
    setMode('register');
  };
  
  const handleTermsClose = () => {
    setShowTermsModal(false);
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const result = await login(email, password);
        if (result.success) {
          if (onSuccess) onSuccess();
          onClose();
        } else {
          setError(result.error);
        }
      } else {
        // Register mode
        if (!name.trim()) {
          setError(t('errors.pleaseEnterName'));
          setLoading(false);
          return;
        }
        
        if (securityQuestion1 || securityQuestion2 || answer1 || answer2) {
          if (!securityQuestion1 || !answer1 || !securityQuestion2 || !answer2) {
            setError(t('errors.completeBothSecurityQuestions'));
            setLoading(false);
            return;
          }
          if (securityQuestion1 === securityQuestion2) {
            setError(t('errors.selectDifferentQuestions'));
            setLoading(false);
            return;
          }
        }
        
        const securityQuestions = (securityQuestion1 && answer1 && securityQuestion2 && answer2) ? {
          question1: securityQuestion1,
          answer1: answer1,
          question2: securityQuestion2,
          answer2: answer2
        } : null;
        
        const result = await register(email, password, name, securityQuestions, {
          terms_accepted: termsAccepted,
          terms_version: acceptedTermsVersion,
          terms_hash: acceptedTermsHash
        });
        
        if (result.success) {
          if (onSuccess) onSuccess();
          onClose();
        } else {
          setError(result.error);
        }
      }
    } catch (error) {
      setError(t('errors.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Fetch security questions by email
  const handleFetchQuestions = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/get-security-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setFetchedQ1(data.question_1);
        setFetchedQ2(data.question_2);
        setResetStep(2);
      } else {
        setError(data.detail || 'No security questions found for this account.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Submit security answers + new password
  const handleResetWithQuestions = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotEmail,
          security_question_1: fetchedQ1,
          security_answer_1: forgotAnswer1,
          security_question_2: fetchedQ2,
          security_answer_2: forgotAnswer2,
          new_password: newPassword
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        alert('Password reset successful! You can now login with your new password.');
        setMode('login');
        setPassword(newPassword);
      } else {
        setError(data.detail || 'Failed to reset password.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  return (
    <div className="auth-modal-overlay" data-testid="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" data-testid="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" data-testid="auth-close-button" onClick={onClose}>×</button>
        
        {mode === 'forgot' ? (
          <>
            <h2>Reset Password</h2>
            {error && <div className="auth-error" data-testid="auth-error">{error}</div>}
            
            {resetStep === 1 && (
              <form onSubmit={handleFetchQuestions} data-testid="forgot-email-form">
                <p className="help-text">Enter your email to look up your security questions</p>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="Enter your email"
                    data-testid="forgot-email-input"
                    required
                  />
                </div>
                <button type="submit" className="auth-button" data-testid="forgot-lookup-btn" disabled={loading}>
                  {loading ? 'Looking up...' : 'Find My Questions'}
                </button>
                <button 
                  type="button" 
                  className="link-button"
                  data-testid="forgot-back-to-login"
                  onClick={() => setMode('login')}
                >
                  Back to Login
                </button>
              </form>
            )}

            {resetStep === 2 && (
              <form onSubmit={handleResetWithQuestions} data-testid="forgot-questions-form">
                <p className="help-text">Answer your security questions and choose a new password</p>
                
                <div className="form-group">
                  <label>{fetchedQ1}</label>
                  <input
                    type="text"
                    value={forgotAnswer1}
                    onChange={(e) => setForgotAnswer1(e.target.value)}
                    placeholder="Your answer"
                    data-testid="forgot-answer1-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{fetchedQ2}</label>
                  <input
                    type="text"
                    value={forgotAnswer2}
                    onChange={(e) => setForgotAnswer2(e.target.value)}
                    placeholder="Your answer"
                    data-testid="forgot-answer2-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 chars)"
                    data-testid="forgot-new-password-input"
                    minLength={6}
                    required
                  />
                </div>

                <button type="submit" className="auth-button" data-testid="forgot-reset-btn" disabled={loading}>
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
                <button 
                  type="button" 
                  className="link-button"
                  data-testid="forgot-back-to-step1"
                  onClick={() => setResetStep(1)}
                >
                  Back
                </button>
              </form>
            )}
          </>
        ) : (
          <>
            <h2>{mode === 'login' ? 'Welcome Back!' : 'Create Account'}</h2>
            
            {error && <div className="auth-error" data-testid="auth-error">{error}</div>}
            
            <form onSubmit={handleSubmit} data-testid="auth-form">
          {mode === 'register' && (
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                data-testid="register-name-input"
                required
              />
            </div>
          )}
          
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              data-testid="auth-email-input"
              required
            />
          </div>
          
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              data-testid="auth-password-input"
              required
              minLength={6}
            />
            <small style={{ color: '#aaa', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Minimum 6 characters
            </small>
          </div>
          
          {mode === 'register' && (
            <>
              <div className="security-questions-section">
                <p className="section-label">Security Questions (Optional - for password recovery)</p>
                
                <div className="form-group">
                  <label>Security Question 1</label>
                  <select 
                    value={securityQuestion1}
                    onChange={(e) => setSecurityQuestion1(e.target.value)}
                    data-testid="register-sq1-select"
                  >
                    <option value="">Select a question...</option>
                    {SECURITY_QUESTIONS.map((q) => (
                      <option key={q} value={q}>{q}</option>
                    ))}
                  </select>
                  {securityQuestion1 && (
                    <input
                      type="text"
                      value={answer1}
                      onChange={(e) => setAnswer1(e.target.value)}
                      placeholder="Your answer"
                      data-testid="register-sa1-input"
                    />
                  )}
                </div>

                <div className="form-group">
                  <label>Security Question 2</label>
                  <select 
                    value={securityQuestion2}
                    onChange={(e) => setSecurityQuestion2(e.target.value)}
                    data-testid="register-sq2-select"
                  >
                    <option value="">Select a question...</option>
                    {filteredQuestions2.map((q) => (
                      <option key={q} value={q}>{q}</option>
                    ))}
                  </select>
                  {securityQuestion2 && (
                    <input
                      type="text"
                      value={answer2}
                      onChange={(e) => setAnswer2(e.target.value)}
                      placeholder="Your answer"
                      data-testid="register-sa2-input"
                    />
                  )}
                </div>
              </div>
            </>
          )}
          
          <button type="submit" className="auth-submit" data-testid="auth-submit-btn" disabled={loading}>
            {loading ? 'Please wait...' : (mode === 'login' ? 'Login' : 'Register')}
          </button>
          
          {mode === 'login' && (
            <button 
              type="button" 
              className="forgot-password-link"
              data-testid="forgot-password-link"
              onClick={() => setMode('forgot')}
            >
              Forgot Password?
            </button>
          )}
        </form>
        
        <div className="auth-divider">
          <span>OR</span>
        </div>
        
        <button 
          className="register-button" 
          data-testid="register-button"
          onClick={handleSwitchToRegister} 
          disabled={loading}
        >
          <span className="register-icon">&#9997;</span>
          Register
        </button>
        
        <button 
          className="google-login-button"
          data-testid="google-login-button"
          onClick={handleGoogleLogin} 
          disabled={loading}
        >
          <svg width="18" height="18" viewBox="0 0 48 48" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </button>
        
        <button 
          className="guest-login-button"
          data-testid="guest-login-button"
          onClick={handleGuestLogin} 
          disabled={loading}
        >
          <VenetianMask size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Continue as Guest
        </button>
        <p className="guest-login-note" data-testid="guest-login-note">
          Play instantly — no account needed. Nothing is saved when you leave.
        </p>
        
        <div className="auth-switch">
          {mode === 'login' ? (
            <p>
              Don't have an account?{' '}
              <button onClick={handleSwitchToRegister} className="link-button" data-testid="switch-to-register">
                Sign up
              </button>
            </p>
          ) : (
            <p>
              Already have an account?{' '}
              <button onClick={() => setMode('login')} className="link-button" data-testid="switch-to-login">
                Login
              </button>
            </p>
          )}
        </div>

        {/* Escape hatch for users stuck on a stale cached bundle (common for
            installed PWAs that were installed before a new deploy). Unregisters
            service workers, nukes CacheStorage, clears localStorage flags, and
            hard-reloads — after which the flow works normally. */}
        <div className="auth-reset-help" data-testid="auth-reset-help">
          <button
            type="button"
            className="link-button-subtle"
            data-testid="auth-reset-app-btn"
            onClick={async () => {
              if (!window.confirm("Reset the app? This clears your local cache and reloads the latest version. You'll stay signed in via your secure cookie.")) return;
              try {
                if ('serviceWorker' in navigator) {
                  const regs = await navigator.serviceWorker.getRegistrations();
                  await Promise.all(regs.map(r => r.unregister().catch(() => {})));
                }
              } catch { /* ignore */ }
              try {
                if (typeof caches !== 'undefined') {
                  const names = await caches.keys();
                  await Promise.all(names.map(n => caches.delete(n).catch(() => {})));
                }
              } catch { /* ignore */ }
              try {
                Object.keys(localStorage).forEach(k => {
                  if (k.startsWith('ckk_app_version') || k.startsWith('ckk_reloaded') || k.startsWith('ckk_pwa_install') || k.startsWith('chessKingdom')) {
                    localStorage.removeItem(k);
                  }
                });
                sessionStorage.clear();
              } catch { /* ignore */ }
              window.location.reload();
            }}
          >
            Having trouble signing in? Reset app &amp; reload
          </button>
        </div>
        </>
      )}
      
      <TermsModal
        isOpen={showTermsModal}
        onAccept={handleTermsAccept}
        onClose={handleTermsClose}
        termsData={termsData}
      />
      </div>
    </div>
  );
};

export default AuthModal;
